import { useFormikContext } from 'formik';
import { useNavigate } from 'react-router-dom';

import { ErrorField } from '@/components/error-field';
import { RequiredLabel } from '@/components/required-label';
import { ShowHideButton } from '@/components/show-hide';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';

import { useToggle } from '@/hooks/use-toggle';

import { useFormikError } from '@/lib/error';

type Props = {
  isLoading: boolean;
};

const SubmitButton = ({ isLoading }: { isLoading: boolean }) => {
  const { isValid } = useFormikContext();

  return (
    <Button type="submit" className="w-full mt-4" disabled={isLoading || !isValid}>
      Reset password
    </Button>
  );
};

export const ResetPasswordForm = ({ isLoading }: Props) => {
  const { state: newPasswordState, toggle: toggleNewPassword } = useToggle();
  const { state: confirmNewPasswordState, toggle: toggleConfirmNewPassword } = useToggle();
  const navigate = useNavigate();

  const { getFieldProps } = useFormikContext();

  const hasError = useFormikError();

  return (
    <>
      <div className="grid grid-cols-2 gap-3 items-start">
        <div className="grid gap-3">
          <Field aria-invalid={hasError('first_name')}>
            <RequiredLabel htmlFor="first_name">First name</RequiredLabel>
            <Input
              id="first_name"
              placeholder="First name"
              type="text"
              {...getFieldProps('first_name')}
              aria-invalid={hasError('first_name')}
            />
            <ErrorField name="first_name" />
          </Field>
        </div>
        <div className="grid gap-3">
          <Field aria-invalid={hasError('last_name')}>
            <RequiredLabel htmlFor="last_name">Last name</RequiredLabel>
            <Input
              id="last_name"
              placeholder="Last name"
              type="text"
              {...getFieldProps('last_name')}
              aria-invalid={hasError('last_name')}
            />
            <ErrorField name="last_name" />
          </Field>
        </div>
      </div>

      <div className="grid gap-3 mt-4">
        <Field aria-invalid={hasError('new_password')}>
          <div className="flex items-center">
            <RequiredLabel htmlFor="new_password">New password</RequiredLabel>
          </div>

          <div className="relative">
            <InputGroup>
              <InputGroupInput
                type={newPasswordState ? 'text' : 'password'}
                id="new_password"
                {...getFieldProps('new_password')}
                placeholder="Password"
                aria-invalid={hasError('new_password')}
              />
              <InputGroupAddon align="inline-end">
                <ShowHideButton state={newPasswordState} toggle={toggleNewPassword} />
              </InputGroupAddon>
            </InputGroup>
          </div>
          <ErrorField name="new_password" />
        </Field>
      </div>

      <div className="grid gap-3 mt-4">
        <Field aria-invalid={hasError('confirm_new_password')}>
          <div className="flex items-center">
            <RequiredLabel htmlFor="confirm_new_password">Confirm password</RequiredLabel>
          </div>

          <div className="relative">
            <InputGroup>
              <InputGroupInput
                type={confirmNewPasswordState ? 'text' : 'password'}
                id="confirm_new_password"
                {...getFieldProps('confirm_new_password')}
                placeholder="Confirm password"
                aria-invalid={hasError('confirm_new_password')}
              />
              <InputGroupAddon align="inline-end">
                <ShowHideButton state={confirmNewPasswordState} toggle={toggleConfirmNewPassword} />
              </InputGroupAddon>
            </InputGroup>
          </div>
          <ErrorField name="confirm_new_password" />
        </Field>
      </div>

      <SubmitButton isLoading={isLoading} />

      <Button
        type="button"
        variant="ghost"
        className="w-full"
        onClick={() => {
          navigate('/login');
        }}
      >
        Back to login
      </Button>
    </>
  );
};
